import EventEmitter from 'eventemitter3';
import PQueue from 'p-queue';
import type {
  ReadableStream,
  ReadableStreamBYOBReader,
} from 'web-streams-polyfill';

import { AbortList } from '@iconik-io/core/src/abort_controller/abort_list';
import { shallowEqual } from '@iconik-io/core/src/shallow_equal';
import type { RequiredBy } from '@iconik-io/core/src/types';
import type { IconikApiClientInterface } from '@iconik-io/iconik-api/src/client';
import { AbortEndpointRequestError } from '@iconik-io/iconik-api/src/client/response';

import type {
  ProgressEvent,
  UploaderHttpClientInterface,
} from '../client/client';
import type { CollectionsServiceInterface } from '../collections_service';
import {
  AbortUploadError,
  EmptyAssetIdError,
  EmptyMetaError,
  FailedUploadError,
  StreamReaderChunkError,
  UninitializedChunkError,
  UninitializedChunkMetaError,
  UninitializedChunksError,
  UninitializedStreamReaderError,
  UnsupportedUploadError,
} from '../errors';
import {
  type CalculateChecksum,
  type GetSourceStream,
  UPLOADER_CHUNK_STATUS_COMPLETED,
  UPLOADER_CHUNK_STATUS_WAITING,
  UPLOADER_STATUS_CALCULATING_CHECKSUM,
  UPLOADER_STATUS_COMPLETED,
  UPLOADER_STATUS_COMPLETING,
  UPLOADER_STATUS_FAILED,
  UPLOADER_STATUS_PAUSED,
  UPLOADER_STATUS_UPLOADING,
  type UploaderChunkState,
  type UploaderState,
  type UploaderStatus,
} from '../state';
import {
  getUploadEntityId,
  unwrapUploadEntityId,
  updateUploadEntityId,
} from '../state/upload_entity_id';
import type { StorageOptions } from '../types';
import {
  resetChunks,
  resetIncompleteChunks,
} from '../utils/reset_incomplete_chunks';
import type {
  EntitiesLifecycle,
  EntitiesLifecycleContext,
} from './entities_lifecycle/entities_lifecycle';
import { fileEntitiesLifecycle } from './entities_lifecycle/file_entities_lifecycle';
import {
  fileMultipartEndpoints,
  type MultipartEndpoints,
} from './entity_endpoints';
import type {
  MultipartUploaderEvents,
  MultipartUploaderEventsMap,
} from './events';
import {
  calculateUploadChecksum,
  reuseAssetByChecksum,
} from './reuse_by_checksum';

export type MultipartUploaderParams<Meta, ChunkMeta> = {
  apiClient: IconikApiClientInterface;
  httpClient: UploaderHttpClientInterface;
  collectionsManager: CollectionsServiceInterface;
  getSourceStream: GetSourceStream;
  calculateChecksum?: CalculateChecksum;
  state: UploaderState<Meta, ChunkMeta>;
  storageOptions: StorageOptions;
  /** Which entity family the multipart endpoints address. Defaults to files. */
  endpoints?: MultipartEndpoints;
  /** The iconik-side lifecycle of the upload. Defaults to uploading a file. */
  entitiesLifecycle?: EntitiesLifecycle;
};

export type ChunkUpdate<ChunkMeta> = RequiredBy<
  Partial<UploaderChunkState<ChunkMeta>>,
  'index'
>;

/**
 * 50 MB chunk size as default.
 * Can be altered per uploader basis in options.chunkSize or
 * in getChunkSize method when storage service has some complex
 * requirements.
 */
export const DEFAULT_CHUNK_SIZE = 50 * 1024 * 1024;

export const DEFAULT_CHUNK_CONCURRENCY = 5;

export abstract class MultipartUploader<
  Meta = unknown,
  ChunkMeta = unknown,
> extends EventEmitter<MultipartUploaderEventsMap<Meta, ChunkMeta>> {
  protected chunksQueue: PQueue;

  protected apiClient: IconikApiClientInterface;

  protected httpClient: UploaderHttpClientInterface;

  protected abortList = new AbortList();

  #sequentialReaderQueue = new PQueue({
    concurrency: 1,
  });

  #chunksData = new Map<number, Promise<Uint8Array> | null>();

  #sourceStream: ReadableStream | undefined;

  #sourceStreamReader: ReadableStreamBYOBReader | undefined;

  #lastProgressEvent: ProgressEvent | undefined;

  /**
   * Dummy buffer to read chunks from source stream that are already completed.
   */
  #dummyBuffer: Uint8Array | undefined;

  protected collectionsManager: CollectionsServiceInterface;

  protected getSourceStream: GetSourceStream;

  protected calculateChecksumFn: CalculateChecksum | undefined;

  protected state: UploaderState<Meta, ChunkMeta>;

  protected storageOptions: StorageOptions;

  protected endpoints: MultipartEndpoints;

  protected entitiesLifecycle: EntitiesLifecycle;

  constructor(params: MultipartUploaderParams<Meta, ChunkMeta>) {
    super();
    const {
      apiClient,
      httpClient,
      collectionsManager,
      getSourceStream,
      calculateChecksum,
      state,
      storageOptions,
      endpoints = fileMultipartEndpoints,
      entitiesLifecycle = fileEntitiesLifecycle,
    } = params;

    this.apiClient = apiClient;
    this.httpClient = httpClient;
    this.collectionsManager = collectionsManager;
    this.getSourceStream = getSourceStream;
    this.calculateChecksumFn = calculateChecksum;
    this.state = state;
    this.storageOptions = storageOptions;
    this.endpoints = endpoints;
    this.entitiesLifecycle = entitiesLifecycle;

    this.chunksQueue = new PQueue({
      concurrency:
        state.options.numConcurrentChunks ?? DEFAULT_CHUNK_CONCURRENCY,
    });
  }

  #handleChunkCompleted = (
    event: MultipartUploaderEvents<Meta, ChunkMeta>['chunkCompleted'],
  ) => {
    this.#chunksData.set(event.chunk.index, null);
  };

  init(): void {
    if (!this.state.chunks) {
      // Uploader is new, and we need to initialize a chunks array.
      this.#initChunksArray(this.getChunkSize());
    } else {
      // Chunks state already exists, reset incomplete chunks state.
      this.updateState({ chunks: resetIncompleteChunks(this.state.chunks) });
    }

    this.on('chunkCompleted', this.#handleChunkCompleted);
  }

  /**
   * Makes the upload body available to runUpload. Uploaders that send the whole
   * file in a single request override this to skip the stream reader entirely.
   */
  protected openSource(): void {
    this.#initSourceStreamReader();
  }

  #initSourceStreamReader() {
    const chunkSize = this.unwrapChunk(0).size;

    this.#sourceStream = this.getSourceStream(
      this.state.fileInfo.streamSource,
      {
        chunkSize,
      },
    );
    this.#sourceStreamReader = this.#sourceStream.getReader({ mode: 'byob' });
  }

  #unwrapSourceStreamReader(): ReadableStreamBYOBReader {
    if (!this.#sourceStreamReader) {
      throw new UninitializedStreamReaderError(
        'Stream reader is not initialized, consider running init() method first.',
      );
    }
    return this.#sourceStreamReader;
  }

  /**
   * Capability gate, called at the start of every run. Return false or throw a
   * more specific error when the uploader cannot handle the current state, e.g.
   * a file above the storage service limit.
   */
  protected abstract validate(): boolean;

  protected unwrapAssetId(): string {
    if (!this.state.target.assetId) {
      throw new EmptyAssetIdError('Asset ID is not set');
    }
    return this.state.target.assetId;
  }

  /**
   * Id of the entity the multipart endpoints address: the file id for a file
   * upload, the proxy id for a proxy one.
   */
  protected unwrapUploadEntityId(): string {
    return unwrapUploadEntityId(this.state);
  }

  protected unwrapMeta(): Meta {
    if (!this.state.meta) {
      throw new EmptyMetaError('Meta is not set');
    }
    return this.state.meta;
  }

  protected unwrapChunkMeta(chunkIndex: number): ChunkMeta {
    const chunk = this.unwrapChunk(chunkIndex);
    if (!chunk.meta) {
      throw new UninitializedChunkMetaError(chunk);
    }

    return chunk.meta;
  }

  protected unwrapChunks(): UploaderChunkState<ChunkMeta>[] {
    if (!this.state.chunks) {
      throw new UninitializedChunksError();
    }
    return this.state.chunks;
  }

  protected unwrapChunk(index: number): UploaderChunkState<ChunkMeta> {
    const chunk = this.unwrapChunks().at(index);
    if (!chunk) {
      throw new UninitializedChunksError();
    }
    return chunk;
  }

  protected getIncompleteChunks(): UploaderChunkState<ChunkMeta>[] {
    if (!this.state.chunks) {
      throw new UninitializedChunksError();
    }
    return this.state.chunks.filter(
      (chunk) => chunk.status !== UPLOADER_CHUNK_STATUS_COMPLETED,
    );
  }

  protected updateState(
    stateUpdate: Partial<UploaderState<Meta, ChunkMeta>>,
  ): void {
    this.state = {
      ...this.state,
      ...stateUpdate,
    };

    this.emit('stateUpdated', { state: this.state });

    if (stateUpdate.meta) {
      this.emit('metaUpdated', { state: this.state });
    }
  }

  protected updateChunks(chunkUpdates: ChunkUpdate<ChunkMeta>[]): void {
    if (!this.state.chunks) {
      throw new UninitializedChunksError();
    }

    // Defer emitting the chunk update events until after they have been applied to
    // our state so that there are no inconsistencies between what you can access in the
    // state and what is passed in the event. So store what events to emit in chunkUpdateEvents
    // and signal them when we are done with the state update.
    type ChunkUpdateEvent = {
      name: 'chunkUpdated' | 'chunkCompleted' | 'chunkMetaUpdated';
      state: UploaderState<Meta, ChunkMeta>;
      chunk: UploaderChunkState<ChunkMeta>;
    };
    const chunkUpdateEvents: ChunkUpdateEvent[] = [];
    const chunks = [...this.state.chunks];
    for (const update of chunkUpdates) {
      const index = update.index;
      if (!this.state.chunks[index]) {
        throw new UninitializedChunkError(index);
      }
      const currentChunk = chunks[index];
      const updatedChunk = {
        ...currentChunk,
        ...update,
      };
      chunks[index] = updatedChunk;

      chunkUpdateEvents.push({
        name: 'chunkUpdated',
        state: this.state,
        chunk: updatedChunk,
      });

      if (
        currentChunk.status !== UPLOADER_CHUNK_STATUS_COMPLETED &&
        updatedChunk.status === UPLOADER_CHUNK_STATUS_COMPLETED
      ) {
        chunkUpdateEvents.push({
          name: 'chunkCompleted',
          state: this.state,
          chunk: updatedChunk,
        });
      }

      if (update.meta) {
        chunkUpdateEvents.push({
          name: 'chunkMetaUpdated',
          state: this.state,
          chunk: updatedChunk,
        });
      }
    }

    this.updateState({ chunks });

    for (const event of chunkUpdateEvents) {
      this.emit(event.name, { state: event.state, chunk: event.chunk });
    }
  }

  protected getChunkSize(): number {
    return this.state.options.chunkSize ?? DEFAULT_CHUNK_SIZE;
  }

  /**
   * Function intended to check if the multi part upload still exists on the storage service. This is needed in cases where
   * the upload will expire after some TTL and restarting it after that will fail the upload without being able to recover.
   * Should be overridden in uploaders to perform the actual check against the storage service.
   * @returns true if the upload has expired and needs to be restarted, false otherwise
   */
  abstract checkIfUploadExpired(
    assetId: string,
    entityId: string,
  ): Promise<boolean>;

  /**
   * Recover is intended to recover from any previous upload that was not completed. By default
   * it will reset the chunks that have not been completed to 0 but this can be overridden in
   * uploaders if they need to do more.
   * It will also check if the upload still exists and if not it will clean up the existing file and start a new upload.
   */
  protected async recover() {
    const chunks = this.state.chunks;
    if (chunks) {
      this.updateState({ chunks: resetIncompleteChunks(chunks) });
    }

    // If the entities exist already then we need to check whether the upload
    // they belong to still does. Both ids are read straight off the state
    // rather than through the unwrap helpers, because recovery must no-op when
    // nothing has been created yet instead of throwing.
    const state = this.state;
    const assetId = state.target.assetId;
    const entityId = getUploadEntityId(state);
    if (assetId === undefined || entityId === undefined) {
      return;
    }

    const uploadExpired = await this.checkIfUploadExpired(assetId, entityId);
    if (!uploadExpired) {
      this.emit('log', {
        level: 'info',
        message: 'Upload still exists',
        state: this.state,
      });
      return;
    }

    this.emit('log', {
      level: 'info',
      message:
        'Upload no longer exists. Cleaning up existing entity and creating new.',
      state: this.state,
    });
    await this.#cleanupExpiredUpload(assetId, entityId);
  }

  protected abstract runUpload(): Promise<void>;

  #initChunksArray(chunkSize: number): void {
    const fileSize = this.state.fileInfo.size;

    const chunks: UploaderChunkState<ChunkMeta>[] = [];

    const count = Math.ceil(fileSize / chunkSize);
    for (let i = 0; i < count; i += 1) {
      const start = i * chunkSize;
      let end = (i + 1) * chunkSize;
      end = end > fileSize ? fileSize : end;

      chunks.push({
        index: i,
        status: UPLOADER_CHUNK_STATUS_WAITING,
        start,
        size: end - start,
        uploaded: 0,
      });
    }

    this.emit('log', {
      message: `Initialized chunks array with chunkSize: ${chunkSize} and count: ${count}`,
      level: 'debug',
      state: this.state,
    });

    this.updateState({ chunks });
  }

  #cleanupExpiredUpload = async (assetId: string, entityId: string) => {
    this.emit('log', {
      level: 'debug',
      message: `Deleting existing upload entity ${entityId}`,
      state: this.state,
    });
    await this.apiClient.request(
      this.endpoints.deleteEntity(assetId, entityId),
      { abortSignal: this.abortList.newSignal() },
    );
    this.updateState(updateUploadEntityId(this.state, undefined));

    if (this.state.chunks) {
      this.emit('log', {
        level: 'debug',
        message: 'Resetting chunks',
        state: this.state,
      });
      this.updateState({ chunks: resetChunks(this.state.chunks) });
    }
  };

  #getDummyBuffer(size: number): Uint8Array {
    if (!this.#dummyBuffer || this.#dummyBuffer.length !== size) {
      this.#dummyBuffer = new Uint8Array(size);
    }
    return this.#dummyBuffer;
  }

  protected readChunkData(
    index: number,
    abortSignal: AbortSignal,
  ): Promise<Uint8Array> {
    const reader = this.#unwrapSourceStreamReader();

    // Since we read the file as a stream from start we need to keep track of any reads
    // from file for already completed chunks. Completed chunks will exist if you restart a paused
    // or failed upload. If one of these fail then we need to fail the entire upload since
    // we can't guarantee the integrity of the data.
    const completedChunkPromises = [];
    for (let i = 0; i <= index; i += 1) {
      if (this.#chunksData.has(i)) {
        continue;
      }
      const chunk = this.unwrapChunk(i);
      const isCompletedChunk = chunk.status === UPLOADER_CHUNK_STATUS_COMPLETED;
      const chunkPromise = this.#sequentialReaderQueue.add(
        async () => {
          const buffer = isCompletedChunk
            ? this.#getDummyBuffer(chunk.size)
            : new Uint8Array(chunk.size);

          const { value } = await reader.read(buffer);

          if (abortSignal.aborted) {
            throw new AbortUploadError();
          }

          if (!value) {
            throw new StreamReaderChunkError(
              `Stream reader must enqueue ${chunk.size} bytes for chunk ${chunk.index} with offset ${chunk.start}, no bytes were enqueued`,
            );
          }
          if (value.byteLength !== chunk.size) {
            // TODO this probably could be fixed by sequential read of the
            //  value which is smaller than chunk.size and setting
            //  the data with buffer.set(value, offset)
            throw new StreamReaderChunkError(
              `Stream reader must enqueue ${chunk.size} bytes for chunk ${chunk.index} with offset ${chunk.start}, ${value.byteLength} bytes were enqueued instead`,
            );
          }

          return value;
        },
        { throwOnTimeout: true },
      );
      // Store the completed chunk reads so that we can monitor them for failures.
      if (isCompletedChunk) {
        completedChunkPromises.push(chunkPromise);
      }
      this.#chunksData.set(i, isCompletedChunk ? null : chunkPromise);
    }

    const chunkData = this.#chunksData.get(index);
    if (chunkData === undefined) {
      throw Error(`Requested chunk #${index} data promise wasn't initialized`);
    }

    if (chunkData === null) {
      throw Error(
        `Requested chunk #${index} data is not available as the chunk is completed`,
      );
    }

    return Promise.all(completedChunkPromises).then(() => chunkData);
  }

  protected get entitiesLifecycleContext(): EntitiesLifecycleContext<
    Meta,
    ChunkMeta
  > {
    return {
      apiClient: this.apiClient,
      collectionsManager: this.collectionsManager,
      abortList: this.abortList,
      storageOptions: this.storageOptions,
      calculateChecksum: this.calculateChecksumFn,
      getState: () => this.state,
      updateState: (update) => this.updateState(update),
      emit: this.emit.bind(this),
    };
  }

  protected prepareEntities(): Promise<void> {
    return this.entitiesLifecycle.prepareEntities(
      this.entitiesLifecycleContext,
    );
  }

  protected reuseExistingAsset(): Promise<boolean> {
    return reuseAssetByChecksum(this.entitiesLifecycleContext);
  }

  protected async calculateChecksum(): Promise<void> {
    await calculateUploadChecksum(this.entitiesLifecycleContext);
  }

  #updateStatus(status: UploaderStatus): void {
    this.updateState({ status });
  }

  async run(): Promise<void> {
    this.#updateStatus(UPLOADER_STATUS_UPLOADING);

    try {
      if (!this.validate()) {
        throw new UnsupportedUploadError();
      }

      if (this.state.options.calcUploadChecksum) {
        if (!this.state.checksum) {
          this.#updateStatus(UPLOADER_STATUS_CALCULATING_CHECKSUM);
          await this.calculateChecksum();
        }

        if (await this.reuseExistingAsset()) {
          this.#updateStatus(UPLOADER_STATUS_COMPLETED);
          this.emit('completed', { state: this.state });
          return;
        }

        // if the upload is paused or cancelled during the checksum calc or the
        // reuse check the status would have already changed, so only an
        // unchanged status goes back to uploading
        if (this.state.status === UPLOADER_STATUS_CALCULATING_CHECKSUM) {
          this.#updateStatus(UPLOADER_STATUS_UPLOADING);
        }
      }

      await this.recover();

      this.emit('prepareEntities', { state: this.state });
      await this.prepareEntities();

      this.openSource();

      this.emit('runUpload', { state: this.state });
      await this.runUpload();
    } catch (error) {
      if (
        error instanceof AbortUploadError ||
        error instanceof AbortEndpointRequestError
      ) {
        return;
      }
      await this.#abort();
      await this.#rollbackEntities();

      this.#updateStatus(UPLOADER_STATUS_FAILED);
      if (error instanceof Error) {
        this.emit('error', { state: this.state, error });
      }
      throw new FailedUploadError(error);
    }

    await this.#cleanupResources();

    await this.finalize().catch((error) => {
      this.emit('error', { state: this.state, error });
      this.#updateStatus(UPLOADER_STATUS_FAILED);
      throw error;
    });

    this.#updateStatus(UPLOADER_STATUS_COMPLETED);
    this.emit('completed', { state: this.state });
  }

  async finalize(): Promise<void> {
    await this.entitiesLifecycle.finalize(this.entitiesLifecycleContext);
  }

  async #abort(): Promise<void> {
    if (
      this.state.status !== UPLOADER_STATUS_UPLOADING &&
      this.state.status !== UPLOADER_STATUS_CALCULATING_CHECKSUM &&
      this.state.status !== UPLOADER_STATUS_COMPLETING
    ) {
      return;
    }
    this.abortList.abort(new AbortUploadError());

    await this.#cleanupResources();
  }

  /**
   * Aborts all requests that are being processed and sets the paused state.
   */
  async pause(): Promise<void> {
    this.#abort();
    this.updateState({
      status: UPLOADER_STATUS_PAUSED,
    });
    this.emit('paused', { state: this.state });
  }

  protected abstract runCancel(): Promise<void>;

  async cancel(): Promise<void> {
    this.#abort();

    if (this.state.status === UPLOADER_STATUS_COMPLETED) {
      return;
    }

    await this.runCancel();
    await this.#cleanupEntities();

    this.emit('cancelled', { state: this.state });
  }

  /**
   * Clean up local resources like internal buffers and file handles.
   */
  async #cleanupResources(): Promise<void> {
    await this.#sourceStreamReader?.cancel().catch((error) => {
      this.emit('log', {
        level: 'warn',
        message: 'Failed canceling source stream',
        error,
        state: this.state,
      });
    });
    this.#sequentialReaderQueue.clear();
    this.chunksQueue.clear();
    this.#chunksData.clear();
    this.#dummyBuffer = undefined;
  }

  async #cleanupEntities(): Promise<void> {
    await this.entitiesLifecycle.cleanupEntities(this.entitiesLifecycleContext);
  }

  /**
   * Undoes what preparing the upload changed on pre-existing entities. Runs
   * only when the upload has failed for good, never when it is paused.
   */
  async #rollbackEntities(): Promise<void> {
    await this.entitiesLifecycle
      .rollbackEntities?.(this.entitiesLifecycleContext)
      .catch((error) => {
        this.emit('log', {
          level: 'warn',
          message: 'Failed rolling back prepared entities',
          error,
          state: this.state,
        });
      });
  }

  protected uploadChunkProgress(
    chunkIndex: number,
    event: ProgressEvent,
  ): void {
    if (this.state.status === UPLOADER_STATUS_PAUSED) {
      return;
    }

    this.updateChunks([{ index: chunkIndex, uploaded: event.loaded }]);

    const chunks = this.unwrapChunks();
    let loaded = 0;
    let total = 0;
    chunks.forEach((item) => {
      loaded += item.uploaded;
      total += item.size;
    });

    const progressEvent: ProgressEvent = {
      lengthComputable: true,
      loaded,
      total,
    };

    if (shallowEqual(progressEvent, this.#lastProgressEvent)) {
      return;
    }

    this.#lastProgressEvent = progressEvent;

    this.emit('progress', { state: this.state, event: progressEvent });
  }
}
